import { isItemReviewable } from "@memorang/helpers/item";
import {
	type QuestionItem,
	type QuestionTypeRowData,
	buildItemTypeDetailStats,
	emptyItemTypeDetailStats,
} from "@memorang/helpers/report";
import type { BaseItemSchema, MixedItemTag } from "@memorang/types";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Divider } from "react-native-paper";
import { getQuestionTypeIcon } from "../../../constants";
import { useAppConfig } from "../../../contexts/AppConfigContext";
import { useProficiencyIconMap } from "../../../hooks/useProficiencyIconMap";
import { useAppTheme } from "../../../useAppTheme";
import Box from "../../Box";
import { ItemTypeListItem } from "../../ItemTypeListItem";
import type { SessionReportItemTypeDetailSelection } from "../SessionReportItemTypeDetailDialog";
import {
	buildAggregateScoreChipProps,
	buildMasteryLevelByDistributionMap,
	flattenSessionItemsForLookup,
} from "./helpers";
import {
	buildProficiencyBandFillsForQuestionType,
	getSessionLevelForQuestionType,
	hasProficiencyProgressUi,
	mapFillsToConnectedLevels,
} from "./proficiency-levels";

type Props = {
	rows: QuestionTypeRowData[];
	questionItemsByQuestionType: Record<string, QuestionItem[]>;
	sessionItems: BaseItemSchema[];
	isSummativeTest: boolean;
	proficiencyTags: MixedItemTag[];
	skill?: string;
	onRowPress: (selection: SessionReportItemTypeDetailSelection) => void;
};

export const SessionReportQuestionTypeRowList = ({
	rows,
	questionItemsByQuestionType,
	sessionItems,
	isSummativeTest,
	proficiencyTags,
	skill,
	onRowPress,
}: Props) => {
	const theme = useAppTheme();
	const { t } = useTranslation();
	const {
		masteryLevels,
		proficiencyLevels,
		proficiencyTargetScore = 90,
		reportView,
	} = useAppConfig();
	const proficiencyIconMap = useProficiencyIconMap();

	const masteryLevelByDistribution = useMemo(
		() => buildMasteryLevelByDistributionMap(masteryLevels),
		[masteryLevels],
	);

	const showLevelChip = hasProficiencyProgressUi({
		tags: proficiencyTags,
		sessionItems,
		proficiencyLevels,
	});
	const preferMastery =
		!reportView?.options?.showProficiencyEstimates &&
		masteryLevelByDistribution.size > 0;
	const showDialogLevels = showLevelChip && !preferMastery;

	const flattenedSessionItems = useMemo(
		() => flattenSessionItemsForLookup(sessionItems),
		[sessionItems],
	);

	const derivedByQuestionType = useMemo(
		() =>
			new Map(
				rows.map((row) => {
					const questionItems =
						questionItemsByQuestionType[row.questionType] ?? [];
					const stats = buildItemTypeDetailStats({
						questionItems,
						sessionItems: flattenedSessionItems,
						isSummativeTest,
					});
					const fills = showLevelChip
						? buildProficiencyBandFillsForQuestionType({
								questionItems,
								proficiencyTags,
								sessionItems,
								proficiencyLevels: proficiencyLevels ?? [],
								targetScore: proficiencyTargetScore,
							})
						: [];
					const itemIds = new Set(questionItems.map((item) => item.id));
					return [
						row.questionType,
						{
							stats,
							score: buildAggregateScoreChipProps(stats),
							levelLabel: showLevelChip
								? getSessionLevelForQuestionType(fills)
								: undefined,
							connectedLevels: showDialogLevels
								? mapFillsToConnectedLevels(fills, proficiencyIconMap)
								: undefined,
							reviewItems: flattenedSessionItems.filter(
								(item) =>
									item.id != null &&
									itemIds.has(item.id) &&
									isItemReviewable(item),
							),
						},
					] as const;
				}),
			),
		[
			rows,
			questionItemsByQuestionType,
			flattenedSessionItems,
			isSummativeTest,
			showLevelChip,
			showDialogLevels,
			proficiencyTags,
			sessionItems,
			proficiencyLevels,
			proficiencyTargetScore,
			proficiencyIconMap,
		],
	);

	const handleRowPress = (row: QuestionTypeRowData) => {
		const derived = derivedByQuestionType.get(row.questionType);
		onRowPress({
			questionType: row.questionType,
			skill,
			numItems: row.numItems,
			masteryLevel: masteryLevelByDistribution.get(row.distribution),
			levels: derived?.connectedLevels,
			stats: derived?.stats ?? emptyItemTypeDetailStats,
			reviewItems: derived?.reviewItems ?? [],
		});
	};

	return (
		<>
			{rows.map((row, index) => {
				const derived = derivedByQuestionType.get(row.questionType);
				return (
					<Box key={row.questionType}>
						{index > 0 ? (
							<Divider
								style={{
									marginHorizontal: 16,
									backgroundColor: theme.colors.surfaceVariant,
								}}
							/>
						) : null}
						<ItemTypeListItem
							icon={getQuestionTypeIcon(row.questionType)}
							title={row.questionType}
							subtitle={t("report.questionTypeRowSubtitle", {
								count: derived?.stats.numAnswered ?? 0,
							})}
							level={
								derived?.levelLabel
									? {
											label: derived.levelLabel,
											icon: proficiencyIconMap[derived.levelLabel],
										}
									: undefined
							}
							score={derived?.score}
							onPress={() => handleRowPress(row)}
						/>
					</Box>
				);
			})}
		</>
	);
};
